import { SocialAuthService, SocialUser } from '@abacritt/angularx-social-login';
import { Injectable, OnDestroy } from '@angular/core';
import { AuthComponent } from '@layouts/default/components/auth/auth.component';
import { AuthorizationService } from '@layouts/default/components/auth/auth.service';
import { TranslateService } from '@ngx-translate/core';
import { SocialMediaUser } from '@shared/interfaces/auth/social-media-user.interface';
import { TokenRefresh } from '@shared/interfaces/auth/token-refresh.interface';
import { AppInjector } from '@shared/services/app-injector.service';
import { StorageService } from '@shared/services/storage.service';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';

@Injectable()
export class GoogleLoginOneClickService implements OnDestroy {
  socialAuthService: SocialAuthService;
  authService: AuthorizationService;
  storage: StorageService;
  toast: ToastrService;
  translate: TranslateService;
  someSubscriptions: Subscription = new Subscription();
  socialMediaGoogleAuthStateSubscription$: Subscription = undefined;
  subLogin$: Subscription = undefined;

  constructor(public authComponent: AuthComponent) {
    const injector = AppInjector.getInjector();
    this.socialAuthService = injector.get(SocialAuthService);
    this.authService = injector.get(AuthorizationService);
    this.storage = injector.get(StorageService);
    this.toast = injector.get(ToastrService);
    this.translate = injector.get(TranslateService);
  }

  init(): void {
    if (this.socialMediaGoogleAuthStateSubscription$ === undefined) {
      this.socialMediaGoogleAuthStateSubscription$ = this.socialAuthService.authState.subscribe(
        (user: SocialUser) => {
          if (user) {
            if (user.provider.toLocaleLowerCase() == 'google') {
              const pack: SocialMediaUser = {
                email: user.email,
                id: user.id,
                image: user.photoUrl,
                name: user.name,
                provider: user.provider.toLocaleLowerCase(),
                token: user.idToken,
              };

              this.subLogin$ = this.authService.socialMediaAuth(pack).subscribe({
                next: (token: TokenRefresh) => {
                  this.storage.setToken(token);
                  this.authComponent.userAccess();
                },
                error: (e) => {
                  this.onLoginError(e);
                },
              });
            }
          } else {
            this.socialAuthService
              .signOut()
              .then()
              .catch(() => {});
          }
        }
      );
    }
  }

  onLoginError(error: Error) {
    switch (error?.message) {
      case 'account_blocked': {
        this.toast.error(
          this.translate.instant('AUTH.account_is_blocked'),
          this.translate.instant('AUTH.login_error')
        );
        break;
      }
      case 'wrong_token_passed': {
        this.toast.error(
          this.translate.instant('AUTH.password_will_not_be_changed'),
          this.translate.instant('AUTH.resetting_password_cancel')
        );
        break;
      }
      case 'user_exists': {
        this.toast.error(
          this.translate.instant('AUTH.user_exists'),
          this.translate.instant('AUTH.login_error')
        );
        this.socialAuthService.signOut();
        break;
      }
      case 'wrong_username_or_password': {
        this.toast.error(
          this.translate.instant('AUTH.wrong_username_or_password'),
          this.translate.instant('AUTH.login_error')
        );
        break;
      }
      case 'account_is_blocked_by_administration': {
        this.toast.error(
          this.translate.instant('AUTH.account_is_blocked_by_administration'),
          this.translate.instant('AUTH.login_error')
        );
        break;
      }
      default: {
        this.toast.error(error.message, this.translate.instant('AUTH.login_error'));
      }
    }
  }

  ngOnDestroy(): void {
    if (this.socialMediaGoogleAuthStateSubscription$ !== undefined) {
      this.socialMediaGoogleAuthStateSubscription$.unsubscribe();
    }
    if (this.subLogin$ !== undefined) {
      this.subLogin$.unsubscribe();
    }
  }
}
